import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Calculator } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "Products", href: "#products" },
  { label: "Ad Reach", href: "#reach" },
  { label: "Eco", href: "#eco" },
  { label: "Stories", href: "#stories" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = ({ onQuote, onNav }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNav(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#2E1065]/10 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <button
          className="group"
          onClick={() => go("#top")}
          data-testid="nav-home-link"
        >
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="relative text-sm font-semibold text-[#2E1065]/80 hover:text-[#2E1065] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-[#FF5A36] after:transition-all after:duration-300 hover:after:w-full"
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => go("#reach")}
            className="flex items-center gap-1.5 text-sm font-bold text-[#0D9488] border-2 border-[#0D9488]/30 hover:border-[#0D9488] hover:bg-[#0D9488]/5 rounded-full px-4 py-2 transition-all duration-300"
            data-testid="nav-calculate-reach-button"
          >
            <Calculator className="w-4 h-4" /> Calculate Reach
          </button>
          <button
            onClick={() => onQuote()}
            className="group flex items-center gap-1.5 text-sm font-bold text-white bg-[#FF5A36] hover:bg-[#2E1065] rounded-full px-5 py-2.5 transition-all duration-300 shadow-lg shadow-[#FF5A36]/30 hover:shadow-[#2E1065]/30 hover:-translate-y-0.5"
            data-testid="nav-get-quote-button"
          >
            Get Quote
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
          </button>
        </div>

        <button
          className="lg:hidden p-2 text-[#2E1065]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#FAF7F2]/95 backdrop-blur-xl border-b border-[#2E1065]/10"
            data-testid="nav-mobile-drawer"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-lg text-[#2E1065]"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-2 flex items-center justify-center gap-2 font-bold text-white bg-[#FF5A36] rounded-full px-5 py-3"
                data-testid="nav-mobile-get-quote-button"
              >
                Get Quote <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
